/**
 * Checks, that node is a child of parentElement
 */
function _isChildOf(node, parentElement) {
  while (node !== null) {
    if (node === parentElement) {
      return true;
    }
    node = node.parentNode;
  }

  return false;
}

/**
 * Returns anchorNode for start edge and focusNode for end edge
 */
function _getNodeByEdge(edge: 'start' | 'end', selection: Selection): Node | null {
  return edge === 'start' ? selection.anchorNode : selection.focusNode;
}

/**
 * Returns anchorOffset for start edge and focusOffset for end edge
 */
function _getOffsetByEdge(edge: 'start' | 'end', selection: Selection): number {
  return edge === 'start' ? selection.anchorOffset : selection.focusOffset;
}

function _getNodeValueLength(node: Node | undefined | null): number {
  if (!node) {
    return 0;
  }

  if (isFieldNode(node) && !isFieldNodeText(node)) {
    return node.childNodes?.[0]?.nodeValue?.length ?? 0;
  }

  return node.nodeValue?.length ?? 0;
}

/**
 * Returns cursor position number by edge ignoring Drop Down Editable Fields
 * (just only real characters and spaces calculated)
 */
function _nodeCursorPosition(edge: 'start' | 'end', parentElement: HTMLElement): number {
  let position = -1;

  const selection = window.getSelection();
  if (selection) {
    let node = _getNodeByEdge(edge, selection);
    if (node) {
      if (_isChildOf(node, parentElement)) {
        position = _getOffsetByEdge(edge, selection);
        if (isFieldNode(node)) {
          // we adding 4 here, because of fields looks like {{fieldName}} with 4 bracket characters
          position += 4;
        }

        while (node) {
          if (node === parentElement) {
            break;
          }

          if (node.previousSibling) {
            node = node.previousSibling;
            position += node?.textContent?.length ?? 0;
            if (isFieldNode(node)) {
              // we adding 4 here, because of fields looks like {{fieldName}} with 4 bracket characters
              position += 4;
            }
          } else {
            node = node.parentNode;
            if (node === null) {
              break;
            }
          }
        }
      }
    }
  }

  return position;
}

/**
 * Returns both start and end cursor position as an array,
 * where first element is start cursor position and second element is end cursor positioin
 */
export function getCurrentCursorPosition(parentElement: HTMLElement | null): [number, number] {
  if (!parentElement) {
    return [-1, -1];
  }

  const start = _nodeCursorPosition('start', parentElement);
  const end = _nodeCursorPosition('end', parentElement);

  return [start, end];
}

/**
 * Inserts provided symbol to the end of current selection position, mostly for new line
 */
export function insertNewLineSymbol(): void {
  if (window.getSelection) {
    const selection = window.getSelection();
    if (selection) {
      let node: Node | null | undefined = selection.anchorNode;
      if (isFieldNodeText(node)) {
        node = node?.parentNode;
      }
      const offset = selection.anchorOffset;
      let symbol = '\n';
      if (
        node &&
        offset === selection.focusOffset &&
        _getNodeValueLength(node) === offset &&
        node?.parentNode?.lastChild === node
      ) {
        symbol = '\n\n';
      }

      const textNode = document.createTextNode(symbol);

      if (isFieldNode(node)) {
        if (symbol === '\n\n') {
          node?.parentNode?.appendChild(textNode);
        } else {
          const nextNode = isFieldNode(node) ? getClosestNextNode(node) : selection.focusNode;
          nextNode?.parentNode?.insertBefore(textNode, nextNode);
        }

        const range = document.createRange();
        range.deleteContents();
        range.selectNode(textNode);
        range.setStart(textNode, 1);
        range.setEnd(textNode, 1);
        range.collapse(false);

        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(textNode);
        range.collapse(false);

        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }
}

/**
 * Returns closest previous text (which not a field value) if exists
 */
const getClosestPrevNode = (node: Node | null): Node => {
  return isFieldNode(node)
    ? isFieldNodeText(node)
      ? node?.parentNode?.previousSibling
      : (node as any)?.previousSibling
    : node?.previousSibling?.previousSibling;
};

/**
 * Inserts provided symbol to the start of current selection position
 */
export function addPrevSymbol(symbol = `\u00A0`): void {
  try {
    if (window.getSelection) {
      const selection = window.getSelection();
      if (selection) {
        const range = document.createRange();
        const currentNode = getClosestPrevNode(selection.anchorNode);
        if (currentNode && isFieldNode(currentNode)) {
          const prevField = currentNode?.previousSibling;
          if (prevField) {
            range.selectNode(prevField);
            const space = document.createTextNode(symbol);
            range.deleteContents();
            range.insertNode(space);
            range.collapse(false);

            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      }
    }
  } catch (e) {
    console.error(e);
  }
}

function _jumpToEndOfNode(node: Node): void {
  try {
    if (window.getSelection) {
      const selection = window.getSelection();
      if (selection) {
        const range = document.createRange();
        if (isFieldNode(node)) {
          const textNode: Node = node.childNodes[0];
          if (textNode) {
            range.selectNode(textNode);
            const nodeLastIndex = textNode.nodeValue?.length ?? 0;
            range.setStart(textNode, nodeLastIndex);
            range.setEnd(textNode, nodeLastIndex);
            range.collapse(false);

            selection.removeAllRanges();
            selection.addRange(range);
          }
        } else {
          if (node.nodeValue) {
            range.selectNode(node);
            const nodeLastIndex = node.nodeValue.length;
            range.setStart(node, nodeLastIndex);
            range.setEnd(node, nodeLastIndex);
            range.collapse(false);

            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      }
    }
  } catch (e) {
    console.error(e);
  }
}

/**
 * Jump to start of currently touched field
 */
export function jumpToStart(): void {
  try {
    if (window.getSelection) {
      const selection = window.getSelection();
      if (selection) {
        const prevNode = getClosestPrevNode(selection.anchorNode);
        if (prevNode) {
          _jumpToEndOfNode(prevNode);
        } else {
          const otherPrevNode = selection.anchorNode?.previousSibling || selection.anchorNode;
          if (otherPrevNode && isFieldNode(otherPrevNode)) {
            const range = document.createRange();

            const textNode: Node = isFieldNodeText(otherPrevNode)
              ? otherPrevNode
              : otherPrevNode.childNodes[0];
            if (textNode) {
              range.selectNode(textNode);
              range.setStart(textNode, 0);
              range.setEnd(textNode, 0);
              range.collapse(false);

              selection.removeAllRanges();
              selection.addRange(range);
            }
          }
        }
      }
    }
  } catch (e) {
    console.error(e);
  }
}

/**
 * Test that provided node is a Field Node TEXT
 */
const isFieldNodeText = (node: Node | null | undefined) => {
  return (node?.parentNode as any)?.tagName === 'B';
};

/**
 * Test that provided node is a Field Node (can be editing using drop down list)
 */
export const isFieldNode = (node: Node | null | undefined) => {
  return node?.nodeName === 'B' || isFieldNodeText(node);
};

/**
 * Find next closes text which not a Field Node after current selected Field Node
 */
const getClosestNextNode = (node: Node | null | undefined): Node | null => {
  if (!node) {
    return null;
  }
  return isFieldNode(node)
    ? isFieldNodeText(node)
      ? node?.parentNode?.nextSibling
      : (node as any)?.nextSibling
    : node?.nextSibling;
};

/**
 * Inserts provided symbol to the end of current selection position, to fix default behaviour which trying edit
 * Field content, but we would like to avoid manual editing of Field content, that's why this code
 */
export function addSymbol(symbol = `\u00A0`): void {
  try {
    if (window.getSelection) {
      const selection = window.getSelection();
      if (selection) {
        const range = document.createRange();
        const focusNode = selection.focusNode;
        if (focusNode) {
          if (isFieldNode(focusNode)) {
            if (selection.focusOffset === 0) {
              // It means, that this is first element of content
              const parent = isFieldNodeText(focusNode)
                ? focusNode?.parentNode?.parentNode
                : focusNode.parentNode;
              const anySymbol = document.createTextNode(symbol);

              if (parent) {
                parent.prepend(anySymbol);

                range.selectNode(anySymbol);
                range.setStart(anySymbol, 1);
                range.setEnd(anySymbol, 1);
                range.deleteContents();
                range.collapse(false);

                selection.removeAllRanges();
                selection.addRange(range);
                return;
              }
            }
          }
          const closestNextNode = isFieldNode(focusNode)
            ? getClosestNextNode(focusNode)
            : focusNode;
          if (closestNextNode) {
            if (isFieldNode(closestNextNode)) {
              const anySymbol = document.createTextNode(symbol);
              closestNextNode?.parentNode?.insertBefore(anySymbol, closestNextNode);

              range.selectNode(anySymbol);
              range.setStart(anySymbol, 1);
              range.setEnd(anySymbol, 1);
              range.deleteContents();
              range.collapse(false);

              selection.removeAllRanges();
              selection.addRange(range);
            } else {
              range.selectNode(closestNextNode);
              const anySymbol = document.createTextNode(symbol);
              range.insertNode(anySymbol);
              range.setStart(anySymbol, 1);
              range.setEnd(anySymbol, 1);
              range.deleteContents();
              range.collapse(false);

              selection.removeAllRanges();
              selection.addRange(range);
            }
          } else {
            let fieldNode: any = focusNode;
            if (isFieldNodeText(focusNode)) {
              fieldNode = focusNode.parentNode;
            }

            const anySymbol = document.createTextNode(symbol);
            fieldNode.parentNode.appendChild(anySymbol);
            range.selectNode(anySymbol);
            range.collapse(false);

            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      }
    }
  } catch (e) {
    console.error(e);
  }
}

/**
 * Jumps to the end of current selected Field
 */
export function jumpToEnd(): void {
  try {
    if (window.getSelection) {
      const selection = window.getSelection();
      if (selection) {
        if (selection.focusNode) {
          const node = getClosestNextNode(selection.focusNode);
          if (node) {
            if (isFieldNode(node)) {
              _jumpToEndOfNode(node);
            } else {
              const range = document.createRange();

              range.selectNode(node);
              range.setStart(node, 0);
              range.setEnd(node, 0);
              range.collapse(false);

              selection.removeAllRanges();
              selection.addRange(range);
            }
          }
        }
      }
    }
  } catch (e) {
    console.error(e);
  }
}

/**
 * Remove previous Field, before current cursor
 */
export function removePrev(): void {
  try {
    if (window.getSelection) {
      const selection = window.getSelection();
      if (selection) {
        const anchorNode = selection?.anchorNode;
        const isField = isFieldNode(anchorNode);
        if (isField) {
          if (isFieldNodeText(anchorNode)) {
            if (anchorNode?.parentNode) {
              (anchorNode.parentNode as any).remove();
            }
          } else {
            (anchorNode as any).remove();
          }
        } else {
          if (anchorNode?.previousSibling) {
            if (isFieldNode(anchorNode.previousSibling)) {
              (anchorNode.previousSibling as any).remove();
            }
          }
        }
      }
    }
  } catch (e) {
    console.error(e);
  }
}

/**
 * Remove next Field, after current cursor
 */
export function removeNext(): void {
  try {
    if (window.getSelection) {
      const selection = window.getSelection();
      if (selection) {
        if ((selection?.focusNode as any)?.nextElementSibling) {
          ((selection?.focusNode as any)?.nextElementSibling as any).remove();
        }
      }
    }
  } catch (e) {
    console.error(e);
  }
}

function _setSelection(node: Node, _pos: number) {
  const pos = _pos < 0 ? 0 : _pos;
  if (window.getSelection) {
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();

      const range = document.createRange();

      range.selectNode(node);
      range.setStart(node, pos);
      range.setEnd(node, pos);
      selection.addRange(range);
    }
  }
}

/**
 * Set cursor position to provided _node at provided position (ignoring Field brackets {{field}})
 */
export function setCursorPosition(_node: HTMLElement, _position: number) {
  const position = _position === Infinity ? 0 : _position;
  try {
    let index = 0;
    let offset = position;
    let node: any = _node.childNodes[index];
    if (!node) {
      _setSelection(_node, 0);
    }
    while (node) {
      if (isFieldNode(node)) {
        node = node.firstChild;
        offset -= node.nodeValue?.length;
        offset -= 4;
        index++;
        node = _node.childNodes[index];
        if (!node) {
          const prevNode = _node.childNodes[index - 1]?.childNodes?.[0];
          if (prevNode) {
            _setSelection(prevNode, prevNode?.nodeValue?.length ?? 0);
          }
        }
        continue;
      }

      if (node?.nodeValue?.length < offset) {
        offset -= node.nodeValue?.length;
        index++;
        node = _node.childNodes[index];
        if (!node) {
          const prevNode = _node.childNodes[index - 1];
          if (prevNode) {
            _setSelection(prevNode, prevNode.nodeValue?.length ?? 0);
          }
        }
      } else {
        if (node) {
          _setSelection(node, offset);
        }
        break;
      }
    }
  } catch (e) {
    console.warn(e);
    _setSelection(_node, 0);
  }
}

const isTextSelected = (selection: Selection): boolean => {
  return (
    selection.focusNode !== selection.anchorNode || selection.focusOffset !== selection.anchorOffset
  );
};

export const getPrevIsField = () => {
  if (window.getSelection) {
    const selection = window.getSelection();
    if (selection) {
      if (isTextSelected(selection)) {
        return false;
      }

      const isField = isFieldNode(selection.anchorNode);
      const isFirstSymbol = selection?.anchorOffset === 0;
      const prevElement = selection?.anchorNode?.previousSibling;
      return isField || (isFirstSymbol && prevElement && isFieldNode(prevElement));
    }
  }

  return false;
};

export const getNextIsField = () => {
  if (window.getSelection) {
    const selection = window.getSelection();
    if (selection) {
      if (isTextSelected(selection)) {
        return false;
      }

      const isLast = selection.focusOffset === selection.focusNode?.nodeValue?.length;
      const isField = isFieldNode(selection.anchorNode);

      if (isLast) {
        const nextNode = getClosestNextNode(selection.focusNode);
        if (!nextNode) {
          return false;
        }

        return isFieldNode(nextNode);
      } else {
        return isField;
      }
    }
  }

  return false;
};

export const getCurrentIsField = () => {
  if (window.getSelection) {
    const selection = window.getSelection();
    if (selection) {
      if (isTextSelected(selection)) {
        return false;
      }
      if (selection.anchorNode?.nodeName === 'DIV') {
        if (selection.anchorNode?.childNodes?.[0] && selection.anchorOffset === 0) {
          return isFieldNode(selection.anchorNode?.childNodes?.[0]);
        }
        return false;
      }
      return isFieldNode(selection.anchorNode);
    }
  }

  return false;
};
